/**
 * The two route tables, empty at bootstrap.
 *
 * `/implement frontend-page <repo>::dashboard::<page>` appends ONE line per
 * page here, importing that feature's `*.route.tsx`. This file and
 * `index.tsx` are the only places a feature is referenced by name — every
 * other module imports from `@shared/…` alone.
 *
 * File-name rule (frontend-layout.md §10.6): one `RouteObject` →
 * `<page>.route.tsx` (singular); an aggregate array → `<feature>.routes.tsx`
 * (plural). The importer knows the shape from the name.
 */
import type { RouteObject } from 'react-router-dom';

import { authRoutes } from '@features/auth/auth.routes';
import { caseRoutes } from '@features/cases/cases.routes';
import { catalogueRoutes } from '@features/catalogue/catalogue.routes';
import { editorialRoutes } from '@features/editorial/editorial.routes';
import { homeRoute } from '@features/home/home.route';
import { titleRoute } from '@features/title/title.route';
import { inboxRoute } from '@features/inbox/inbox.route';
import { listsRoutes } from '@features/lists/lists.routes';
import { peopleRoute } from '@features/people/people.route';
import { profileRoutes } from '@features/profile/profile.routes';
import { settingsRoutes } from '@features/settings/settings.routes';
import { subscriptionRoutes } from '@features/subscription/subscription.routes';
import { systemRoutes } from '@features/system/system.routes';

/** Guarded, inside the app shell — the subscriber surfaces of the sitemap. */
export const appShellRoutes: RouteObject[] = [
  homeRoute,
  // The gated title page. Its segment is a ContentItem ULID, not a slug —
  // `backend-content` keys both title routes on `{contentItemId}` and publishes
  // no slug → id resolver.
  titleRoute,
  // `/search`, `/genres/:slug` and `/tags/:slug` — the catalogue's own browse
  // surfaces. They used to be cross-host links to `shahedni.com`, which cost the
  // reader their session's whole client state to run a query this host can run,
  // and reached that host's 404 for every Arabic genre slug. All three are
  // disjoint in their first segment, so no matcher ordering decides between them.
  ...catalogueRoutes,
  // `/blog` and `/blog/:slug` — the editorial listing and one post. They used
  // to be a cross-host link to `shahedni.com`, which opened a new tab with no
  // session and could only offer a signed-in reader a sign-in button. The
  // catalogue's browse surfaces moved here first, for the same reason. The two
  // patterns are disjoint at their second segment, so no matcher ordering
  // decides between them or against anything above.
  ...editorialRoutes,
  // `/people/:slug` — one credited person. It used to be a cross-host link to
  // `shahedni.com`, which opened a new tab with no session AND reached that
  // host's 404 for every Arabic slug. Disjoint in its first segment from
  // everything above and below it.
  peopleRoute,
  inboxRoute,
  // `lists` and `lists/:slug` are disjoint patterns, so the pair is listed in
  // sitemap order rather than in a matcher-driven one.
  ...listsRoutes,
  ...profileRoutes,
  // The three settings routes are disjoint literals — `/settings`,
  // `/settings/security`, `/settings/notifications` — so no matcher ordering
  // decides between them and the trio is listed in sitemap order.
  ...settingsRoutes,
  // `cases/new` is aggregated ahead of `cases/:id`: both are one segment past
  // `cases`, and the literal must never be read as a case whose id is `new`.
  ...caseRoutes,
  // `subscriptionRoutes` is an ARRAY — `/plans`, `/checkout` and
  // `/subscription` are three sibling URLs rather than a nested tree, and the
  // `subscription/` folder is file organisation. The plural file name is what
  // says so at the import site. All three are disjoint literals, so no matcher
  // ordering decides between them or against anything above.
  ...subscriptionRoutes,
];

/** Unguarded, inside the auth half-shell — `/login`, `/login/verify`, `/recover`. */
export const authShellRoutes: RouteObject[] = [...authRoutes];

/**
 * Unguarded, inside the FULL app shell — `/404`, `/500`, and the catch-all.
 *
 * A third branch rather than a third entry in `appShellRoutes`, because these
 * two carry the whole shell and no guard: sending an anonymous visitor from a
 * dead address to `/login?next=<dead address>` answers a question they did not
 * ask, and lands them on a sign-in screen whose «next» goes nowhere.
 */
export const systemShellRoutes: RouteObject[] = systemRoutes;
